import React, { useState, useEffect } from 'react';
import './DataMappingExcel.css';

const DataMappingExcel = ({
  onDataExtracted,
  onCancel,
  targetLabels = [],
  parentLabels = [],
  childLabels = [],
  title = "Excel Data Extraction",
  subtitle = "Upload and extract data from Excel files with advanced matching",
  initialFile = null
}) => {
  const [step, setStep] = useState('sheets'); // 'sheets', 'mapping', 'manual', 'extract'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetPreview, setSheetPreview] = useState(null);
  const [extractionOptions, setExtractionOptions] = useState({
    fuzzyThreshold: 0.85,
    maxRowScan: 10,
    enableMultiRowHeaders: true,
    caseSensitive: false
  });
  const [extractionResults, setExtractionResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [livePreview, setLivePreview] = useState(null);
  const [highlightedCells, setHighlightedCells] = useState({ labels: {}, values: {} });
  // Manual mapping state: user-defined mappings and active mapping mode
  const [manualMappings, setManualMappings] = useState({});
  const [mappingMode, setMappingMode] = useState(null);
  // Template management state and handlers
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');

  // Default BHA target labels if none provided
  const defaultTargetLabels = [
    'Well Name', 'Depth In', 'Depth Out', 'Bit Size', 'Bit Type', 
    'Serial No', 'OD', 'ID', 'Length', 'Weight', 'Grade', 
    'Connection', 'Manufacturer', 'Model', 'Description'
  ];

  const currentTargetLabels = targetLabels.length > 0 ? targetLabels : defaultTargetLabels;

  // Debug: Log target labels information
  useEffect(() => {
    console.log('🎯 TARGET LABELS DEBUG:');
    console.log('📋 Provided targetLabels:', targetLabels);
    console.log('📋 Default targetLabels:', defaultTargetLabels);
    console.log('📋 Current targetLabels being used:', currentTargetLabels);
    console.log('📊 Total labels to search for:', currentTargetLabels.length);
    console.log('📝 Full list:', currentTargetLabels.map((label, index) => `${index + 1}. "${label}"`).join('\n'));
  }, [targetLabels, currentTargetLabels]);

  // Auto-process initial file if provided
  useEffect(() => {
    if (initialFile && !uploadedFile) {
      console.log('📁 Processing initial file:', initialFile.name);
      setUploadedFile(initialFile);
      uploadFile(initialFile);
    }
  }, [initialFile, uploadedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update live preview when sheet preview or options change
  useEffect(() => {
    if (sheetPreview && step === 'mapping') {
      updateLivePreview();
    }
  }, [sheetPreview, extractionOptions, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle file drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = async (file) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploadedFile(file);
    setError(null);
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('excelFile', file);

      const response = await fetch('/api/excel-mapping/upload', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setFileInfo(result.fileInfo);
        setAvailableSheets(result.availableSheets);
        setSheetPreview(result.preview);
        
        // Auto-select first sheet if only one sheet
        if (result.availableSheets.length === 1) {
          const sheetName = result.availableSheets[0].name;
          setSelectedSheet(sheetName);
          
          // Auto-trigger scan for single sheet
          console.log('🔄 Auto-scanning single sheet:', sheetName);
          const analysis = analyzePreviewData(result.preview, currentTargetLabels, extractionOptions);
          setLivePreview(analysis);
          setHighlightedCells({
            labels: analysis.labels,
            values: analysis.values
          });
          
          setStep('scan-results');
        } else {
          setStep('sheets');
        }
      } else {
        setError(result.error || 'Failed to upload file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSheetSelection = async (sheetName) => {
    setSelectedSheet(sheetName);
    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch('/api/excel-mapping/preview-sheet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filePath: fileInfo.filePath,
          sheetName: sheetName,
          maxRows: 20,
          maxCols: 20
        })
      });

      const result = await response.json();

      if (result.success) {
        setSheetPreview(result.preview);
        
        // Automatically start scanning process with all field names
        console.log('🔄 Auto-starting scan process after sheet selection...');
        console.log('📋 Target labels to scan:', currentTargetLabels);
        console.log('📊 Parent labels:', parentLabels);
        console.log('📊 Child labels:', childLabels);
        
        // Trigger immediate analysis with the preview data
        const analysis = analyzePreviewData(result.preview, currentTargetLabels, extractionOptions);
        setLivePreview(analysis);
        setHighlightedCells({
          labels: analysis.labels,
          values: analysis.values
        });
        
        console.log('✅ Auto-scan complete:', analysis);
        setStep('scan-results');
      } else {
        setError(result.error || 'Failed to load sheet preview');
      }
    } catch (error) {
      console.error('Sheet selection error:', error);
      setError('Failed to load sheet. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Full sheet preview handler
  const handleFullPreview = async () => {
    if (!selectedSheet || !fileInfo || !sheetPreview) return;
    setIsProcessing(true);
    setError(null);
    try {
      const response = await fetch('/api/excel-mapping/preview-sheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filePath: fileInfo.filePath,
          sheetName: selectedSheet,
          maxRows: sheetPreview.totalRows,
          maxCols: sheetPreview.totalCols
        })
      });
      const result = await response.json();
      if (result.success) {
        setSheetPreview(result.preview);
      } else {
        setError(result.error || 'Failed to load full preview');
      }
    } catch (err) {
      console.error('Full preview error:', err);
      setError('Failed to load full preview. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExtractData = async () => {
    if (!selectedSheet || !fileInfo) {
      setError('Please select a sheet first');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch('/api/excel-mapping/extract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filePath: fileInfo.filePath,
          sheetName: selectedSheet,
          targetLabels: currentTargetLabels,
          options: extractionOptions
        })
      });

      const result = await response.json();

      if (result.success) {
        setExtractionResults(result);
        setStep('manual');
      } else {
        setError(result.error || 'Failed to extract data');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      setError('Failed to extract data. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplyResults = () => {
    // Use frontend detection results instead of backend results
    if (livePreview && livePreview.mappings && livePreview.mappings.length > 0) {
      console.log('🎯 Using Frontend Detection results:', livePreview.mappings);
      
      // Convert frontend mappings to the expected format
      const frontendMappings = {};
      livePreview.mappings.forEach(mapping => {
        frontendMappings[mapping.label] = mapping.value;
      });
      
      const frontendResults = {
        success: true,
        results: {
          mappings: frontendMappings,
          metadata: {
            totalLabelsSearched: currentTargetLabels.length,
            labelsFound: livePreview.mappings.length,
            extractionMethod: {},
            confidence: {},
            cellPositions: {},
            suggestions: []
          }
        },
        summary: {
          totalLabels: currentTargetLabels.length,
          foundLabels: livePreview.mappings.length,
          successRate: `${Math.round((livePreview.mappings.length / currentTargetLabels.length) * 100)}%`,
          extractedData: livePreview.mappings.length
        }
      };
      
      // Add metadata for each mapping
      livePreview.mappings.forEach(mapping => {
        frontendResults.results.metadata.extractionMethod[mapping.label] = `frontend-${mapping.method}`;
        frontendResults.results.metadata.confidence[mapping.label] = mapping.similarity;
        frontendResults.results.metadata.cellPositions[mapping.label] = {
          label: mapping.labelPosition,
          value: mapping.valuePosition
        };
      });
      
      // Merge manual mappings over frontend results
      const mergedMappings = { ...frontendResults.results.mappings, ...manualMappings };
      // Recalculate summary
      const mergedFound = Object.keys(mergedMappings).length;
      const mergedSummary = {
        totalLabels: currentTargetLabels.length,
        foundLabels: mergedFound,
        successRate: `${Math.round((mergedFound / currentTargetLabels.length) * 100)}%`,
        extractedData: mergedFound
      };
      // Update metadata for manual entries
      Object.keys(manualMappings).forEach(label => {
        frontendResults.results.metadata.extractionMethod[label] = 'manual';
        frontendResults.results.metadata.confidence[label] = 1;
        frontendResults.results.metadata.cellPositions[label] = null;
      });
      const finalResults = {
        ...frontendResults,
        results: { ...frontendResults.results, mappings: mergedMappings },
        summary: mergedSummary
      };
      console.log('📤 Sending merged results to form:', finalResults);
      onDataExtracted(finalResults);
    } else if (extractionResults && extractionResults.results) {
      console.log('⚠️ Falling back to backend results');
      // Merge manual mappings over backend results
      const backendMappings = { ...extractionResults.results.mappings };
      const mergedMappings = { ...backendMappings, ...manualMappings };
      extractionResults.results.mappings = mergedMappings;
      console.log('📤 Sending merged backend results to form:', extractionResults);
      onDataExtracted(extractionResults);
    }
  };

  // Handler for manual mapping cell selection in preview
  const handleCellClick = (rowIndex, colIndex) => {
    if (!mappingMode || !sheetPreview) return;
    const cellValue = sheetPreview.data[rowIndex][colIndex];
    // Save manual mapping for the active label
    setManualMappings(prev => ({ ...prev, [mappingMode]: cellValue }));
    // Exit mapping mode and return to manual mapping step
    setMappingMode(null);
    setStep('manual');
  };

  const cleanupFile = async () => {
    if (fileInfo?.filePath) {
      try {
        await fetch('/api/excel-mapping/cleanup', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filePath: fileInfo.filePath
          })
        });
      } catch (error) {
        console.error('Cleanup error:', error);
      }
    }
  };

  const handleCancel = async () => {
    await cleanupFile();
    onCancel();
  };

  const resetToUpload = () => {
    setStep('upload');
    setUploadedFile(null);
    setFileInfo(null);
    setAvailableSheets([]);
    setSelectedSheet(null);
    setSheetPreview(null);
    setExtractionResults(null);
    setError(null);
  };

  const getSheetRecommendation = (sheet) => {
    const name = sheet.name.toLowerCase();
    
    // BHA-related sheet patterns
    if (name.includes('bha') || name.includes('assembly') || name.includes('component')) {
      return { recommended: true, reason: 'Contains BHA/Assembly data' };
    }
    
    // Data sheets
    if (name.includes('data') || name.includes('main') || name === 'sheet1') {
      return { recommended: true, reason: 'Main data sheet' };
    }
    
    // Avoid summary/template sheets
    if (name.includes('summary') || name.includes('template') || name.includes('blank')) {
      return { recommended: false, reason: 'Summary/template sheet' };
    }
    
    return { recommended: false, reason: 'Unknown content' };
  };

  // Enhanced smart label matching with unit handling and variations (inspired by extractBHA.js)
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const original1 = str1.trim();
    const original2 = str2.trim();
    
    // Normalize both strings for comparison
    const normalized1 = normalizeLabel(original1);
    const normalized2 = normalizeLabel(original2);
    
    console.log(`🔍 Comparing: "${original1}" vs "${original2}"`);
    console.log(`📝 Normalized: "${normalized1}" vs "${normalized2}"`);
    
    // Exact match after normalization (like extractBHA.js exact match)
    if (normalized1 === normalized2) {
      console.log(`✅ Exact match after normalization: 100%`);
      return 1.0;
    }
    
    // Check for core label match (ignoring units) - high confidence like extractBHA.js
    const core1 = extractCoreLabel(normalized1);
    const core2 = extractCoreLabel(normalized2);
    
    console.log(`🎯 Core labels: "${core1}" vs "${core2}"`);
    
    if (core1 === core2 && core1.length > 2) {
      console.log(`✅ Core label match: 95%`);
      return 0.95;
    }
    
    // Check for partial matches with high confidence
    const partialScore = checkPartialMatches(core1, core2);
    if (partialScore > 0) {
      console.log(`🎯 Partial match score: ${Math.round(partialScore * 100)}%`);
      return partialScore;
    }
    
    // Fall back to Levenshtein distance for fuzzy matching
    const longer = normalized1.length > normalized2.length ? normalized1 : normalized2;
    const shorter = normalized1.length > normalized2.length ? normalized2 : normalized1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = levenshteinDistance(longer, shorter);
    const similarity = (longer.length - distance) / longer.length;
    
    console.log(`📊 Levenshtein similarity: ${Math.round(similarity * 100)}%`);
    
    // Apply extractBHA.js style threshold - only return if above minimum threshold
    return similarity >= 0.6 ? similarity : 0;
  };

  // Normalize labels for better matching
  const normalizeLabel = (label) => {
    return label
      .toLowerCase()
      .trim()
      // Remove extra spaces
      .replace(/\s+/g, ' ')
      // Normalize common abbreviations
      .replace(/\bdiameter\b/g, 'dia')
      .replace(/\bdiametre\b/g, 'dia')
      .replace(/\boutside\s*diameter\b/g, 'od')
      .replace(/\binside\s*diameter\b/g, 'id')
      .replace(/\binternal\s*diameter\b/g, 'id')
      .replace(/\bexternal\s*diameter\b/g, 'od')
      .replace(/\bserial\s*number\b/g, 'serial no')
      .replace(/\bserial\s*no\b/g, 'serial no')
      .replace(/\bpart\s*number\b/g, 'part no')
      .replace(/\bmodel\s*number\b/g, 'model no')
      .replace(/\blength\b/g, 'length')
      .replace(/\bweight\b/g, 'weight')
      .replace(/\bmass\b/g, 'weight')
      // Normalize depth terms
      .replace(/\bdepth\s*in\b/g, 'depth in')
      .replace(/\bdepth\s*out\b/g, 'depth out')
      .replace(/\btop\s*depth\b/g, 'depth in')
      .replace(/\bbottom\s*depth\b/g, 'depth out')
      .replace(/\bfrom\s*depth\b/g, 'depth in')
      .replace(/\bto\s*depth\b/g, 'depth out')
      // Normalize size terms
      .replace(/\bhole\s*size\b/g, 'bit size')
      .replace(/\bbit\s*diameter\b/g, 'bit size')
      .replace(/\bhole\s*diameter\b/g, 'bit size')
      .replace(/\bbore\s*size\b/g, 'bit size')
      .replace(/\bbore\s*diameter\b/g, 'bit size')
      // Normalize connection terms
      .replace(/\bconnection\s*type\b/g, 'connection')
      .replace(/\bthread\s*type\b/g, 'connection')
      .replace(/\bthread\b/g, 'connection')
      // Remove common prefixes/suffixes
      .replace(/^(the|a|an)\s+/g, '')
      .replace(/\s+(type|size|number|no)$/g, '');
  };

  // Extract core label by removing units and extra descriptors
  const extractCoreLabel = (normalizedLabel) => {
    return normalizedLabel
      // Remove units in parentheses: "Hole Size (in)" -> "Hole Size"
      .replace(/\s*\([^)]*\)\s*/g, ' ')
      // Remove units at the end: "Length mm" -> "Length"
      .replace(/\s+(mm|cm|m|km|in|ft|yd|inch|inches|feet|yard|yards|lbs|kg|g|tons?)$/g, '')
      // Remove unit abbreviations: "Size-in" -> "Size"
      .replace(/[-_](mm|cm|m|km|in|ft|yd|lbs|kg|g)$/g, '')
      // Remove measurement indicators
      .replace(/\s+(measurement|value|data|info|information)$/g, '')
      .trim();
  };

  // Check for partial matches with scoring
  const checkPartialMatches = (core1, core2) => {
    if (!core1 || !core2 || core1.length < 3 || core2.length < 3) return 0;
    
    // Check if one contains the other
    if (core1.includes(core2) || core2.includes(core1)) {
      const longer = core1.length > core2.length ? core1 : core2;
      const shorter = core1.length > core2.length ? core2 : core1;
      return Math.max(0.8, shorter.length / longer.length);
    }
    
    // Check for word-by-word matches
    const words1 = core1.split(/\s+/).filter(w => w.length > 2);
    const words2 = core2.split(/\s+/).filter(w => w.length > 2);
    
    if (words1.length === 0 || words2.length === 0) return 0;
    
    let matchingWords = 0;
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (word1 === word2 || 
            word1.includes(word2) || 
            word2.includes(word1) ||
            levenshteinDistance(word1, word2) <= 1) {
          matchingWords++;
          break;
        }
      }
    }
    
    const wordMatchRatio = matchingWords / Math.max(words1.length, words2.length);
    
    // High word match ratio gets high score
    if (wordMatchRatio >= 0.8) return 0.9;
    if (wordMatchRatio >= 0.6) return 0.8;
    if (wordMatchRatio >= 0.4) return 0.7;
    
    return 0;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

    // Smart analysis with header detection and multiple extraction strategies (enhanced with extractBHA.js algorithm)
  const analyzePreviewData = (previewData, targetLabels, options) => {
    if (!previewData?.data) return { labels: {}, values: {}, mappings: [], headerRows: [], analysis: {} };
    
    const { fuzzyThreshold = 0.85, maxRowScan = 10 } = options;
    const labels = {};
    const values = {};
    const mappings = [];
    
    console.log('🔍 SMART ANALYSIS DEBUG (Enhanced with extractBHA.js algorithm):');
    console.log('📊 Preview data rows:', previewData.data.length);
    console.log('📊 Preview data columns (first row):', previewData.data[0]?.length || 0);
    console.log('🎯 Target labels to search for:', targetLabels.length);
    console.log('📝 Target labels list:', targetLabels.map((label, index) => `${index + 1}. "${label}"`).join(', '));
    console.log('⚙️ Fuzzy threshold:', fuzzyThreshold);
    console.log('⚙️ Max row scan:', maxRowScan);
    
    // Extract merged ranges from preview data (if available)
    const mergedRanges = previewData.mergedRanges || [];
    console.log('📊 Merged ranges found:', mergedRanges.length);
    if (mergedRanges.length > 0) {
      console.log('📊 Merged ranges:', mergedRanges.map(range => 
        `R${range.s.r + 1}C${range.s.c + 1}:R${range.e.r + 1}C${range.e.c + 1}`
      ).join(', '));
    }
    
    // Phase 0: Direct extraction using extractBHA.js algorithm (primary method)
    console.log('🎯 PHASE 0: Direct Extraction (extractBHA.js style)');
    const directExtractions = [];
    
    targetLabels.forEach(targetLabel => {
      const extraction = extractValueByLabel(previewData.data, targetLabel, fuzzyThreshold, mergedRanges);
      if (extraction.value) {
        directExtractions.push(extraction);
        console.log(`✅ Direct extraction: "${targetLabel}" → "${extraction.value}" (${extraction.method})`);
      } else {
        console.log(`❌ Direct extraction failed for: "${targetLabel}"`);
      }
    });
    
    console.log(`📊 Direct extraction results: ${directExtractions.length}/${targetLabels.length} found`);
    
    // If direct extraction found most labels, use it as primary result
    if (directExtractions.length >= Math.ceil(targetLabels.length * 0.6)) {
      console.log('🎯 Using direct extraction as primary method');
      
      const directMappings = directExtractions.map(extraction => ({
        label: extraction.label,
        detectedLabel: extraction.detectedLabel,
        value: extraction.value,
        similarity: extraction.similarity,
        labelPosition: extraction.labelPosition,
        valuePosition: extraction.valuePosition,
        method: `direct-${extraction.method}`,
        quality: extraction.quality || getValueQuality(extraction.value),
        extractionType: 'direct'
      }));
      
      const directLabels = {};
      const directValues = {};
      
      directMappings.forEach(mapping => {
        const labelKey = `${mapping.labelPosition.row - 1},${mapping.labelPosition.col - 1}`;
        const valueKey = `${mapping.valuePosition.row - 1},${mapping.valuePosition.col - 1}`;
        
        directLabels[labelKey] = {
          cellKey: labelKey,
          rowIndex: mapping.labelPosition.row - 1,
          colIndex: mapping.labelPosition.col - 1,
          detectedText: mapping.detectedLabel,
          targetLabel: mapping.label,
          similarity: mapping.similarity,
          position: { row: mapping.labelPosition.row - 1, col: mapping.labelPosition.col - 1 }
        };
        
        directValues[valueKey] = {
          value: mapping.value,
          labelKey: labelKey,
          method: mapping.method,
          position: { row: mapping.valuePosition.row - 1, col: mapping.valuePosition.col - 1 },
          quality: mapping.quality,
          extractionType: 'direct'
        };
      });
      
      return {
        labels: directLabels,
        values: directValues,
        mappings: directMappings,
        headerRows: [],
        tableExtractions: [],
        isolatedLabels: directExtractions,
        analysis: {
          totalLabels: targetLabels.length,
          foundLabels: directExtractions.length,
          headerRows: 0,
          tableExtractions: 0,
          isolatedMappings: directExtractions.length,
          totalMappings: directMappings.length,
          qualityMappings: directMappings.length,
          extractionMethod: 'direct'
        },
        rawMappings: directMappings
      };
    }
    
    console.log('🔍 Direct extraction insufficient, falling back to smart analysis');
    
    // Phase 1: Find all label matches and their positions (fallback method)
    const labelMatches = [];
    const rowLabelCounts = {};
    const allCellsChecked = [];
    const similarityResults = [];
    
    console.log('🔍 PHASE 1: Label Detection (Smart Analysis Fallback)');
    
    previewData.data.forEach((row, rowIndex) => {
      rowLabelCounts[rowIndex] = 0;
      
      row.forEach((cell, colIndex) => {
        if (!cell || typeof cell !== 'string') return;
        
        const cellText = cell.trim();
        if (cellText.length === 0) return;
        
        allCellsChecked.push({
          position: `R${rowIndex + 1}C${colIndex + 1}`,
          text: cellText
        });
        
        // Check each target label
        targetLabels.forEach(targetLabel => {
          const similarity = calculateSimilarity(cellText, targetLabel);
          
          similarityResults.push({
            cellText: cellText,
            targetLabel: targetLabel,
            similarity: similarity,
            position: `R${rowIndex + 1}C${colIndex + 1}`,
            passesThreshold: similarity >= fuzzyThreshold
          });
          
          if (similarity >= fuzzyThreshold) {
            const cellKey = `${rowIndex},${colIndex}`;
            
            const labelMatch = {
              cellKey,
              rowIndex,
              colIndex,
              detectedText: cellText,
              targetLabel: targetLabel,
              similarity: similarity,
              position: { row: rowIndex, col: colIndex }
            };
            
            labelMatches.push(labelMatch);
            rowLabelCounts[rowIndex]++;
            
            // Store in labels object
            labels[cellKey] = labelMatch;
            
            console.log(`✅ MATCH FOUND: "${cellText}" → "${targetLabel}" (${Math.round(similarity * 100)}%) at ${labelMatch.cellKey}`);
          }
        });
      });
    });
    
    console.log('📊 PHASE 1 RESULTS:');
    console.log('📋 Total cells checked:', allCellsChecked.length);
    console.log('📝 All cells found:', allCellsChecked.map(cell => `${cell.position}: "${cell.text}"`).join(', '));
    console.log('🎯 Total similarity checks:', similarityResults.length);
    console.log('✅ Matches above threshold:', labelMatches.length);
    
    // Show top similarity results for debugging
    const topSimilarities = similarityResults
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 10);
    
    console.log('🏆 TOP 10 SIMILARITY RESULTS:');
    topSimilarities.forEach((result, index) => {
      console.log(`${index + 1}. "${result.cellText}" vs "${result.targetLabel}" = ${Math.round(result.similarity * 100)}% ${result.passesThreshold ? '✅' : '❌'} (${result.position})`);
    });
    
    // Show which target labels had no matches
    const foundLabels = new Set(labelMatches.map(match => match.targetLabel));
    const missedLabels = targetLabels.filter(label => !foundLabels.has(label));
    
    if (missedLabels.length > 0) {
      console.log('❌ LABELS WITH NO MATCHES:');
      missedLabels.forEach((label, index) => {
        console.log(`${index + 1}. "${label}"`);
      });
    }
    
    console.log('📋 Label matches found:', labelMatches.length);
    console.log('📊 Row label counts:', rowLabelCounts);
    
    // Phase 2: Identify header rows (rows with multiple labels)
    const headerRows = [];
    const minLabelsForHeader = Math.max(2, Math.ceil(targetLabels.length * 0.3)); // At least 30% of labels or 2
    
    Object.entries(rowLabelCounts).forEach(([rowIndex, count]) => {
      if (count >= minLabelsForHeader) {
        headerRows.push({
          rowIndex: parseInt(rowIndex),
          labelCount: count,
          confidence: count / targetLabels.length
        });
      }
    });
    
    // Sort header rows by label count (descending)
    headerRows.sort((a, b) => b.labelCount - a.labelCount);
    
    console.log('📑 Header rows detected:', headerRows);
    
    // Phase 3: Extract data from primary header row (table structure)
    const tableExtractions = [];
    if (headerRows.length > 0) {
      // Use only the row with maximum label count
      const primaryHeader = headerRows[0];
      // Gather exact label matches on this header row
      let headerLabels = labelMatches.filter(match => match.rowIndex === primaryHeader.rowIndex);
      console.log(`📋 Processing header row ${primaryHeader.rowIndex} with ${headerLabels.length} labels`);
      // Dual table mode: add synthetic fuzzy matches for child fields
      if (parentLabels.length > 0 && childLabels.length > 0) {
        const headerRowCells = previewData.data[primaryHeader.rowIndex] || [];
        const existingCols = new Set(headerLabels.map(h => h.colIndex));
        headerRowCells.forEach((cell, colIndex) => {
          if (!existingCols.has(colIndex) && cell && typeof cell === 'string' && cell.trim()) {
            let bestSim = 0, bestChild = null;
            childLabels.forEach(child => {
              const sim = calculateSimilarity(cell, child);
              if (sim > bestSim) { bestSim = sim; bestChild = child; }
            });
            if (bestChild && bestSim >= fuzzyThreshold) {
              const cellKey = `${primaryHeader.rowIndex},${colIndex}`;
              const syntheticMatch = {
                cellKey,
                rowIndex: primaryHeader.rowIndex,
                colIndex,
                detectedText: cell.trim(),
                targetLabel: bestChild,
                similarity: bestSim,
                position: { row: primaryHeader.rowIndex, col: colIndex }
              };
              headerLabels.push(syntheticMatch);
              labels[cellKey] = syntheticMatch;
              console.log(`🔀 Synthetic header match: "${cell.trim()}" → "${bestChild}" (${Math.round(bestSim * 100)}%) at ${cellKey}`);
            }
          }
        });
      }
      // Extract column data below this header
      headerLabels.forEach(headerLabel => {
        const columnValues = extractColumnData(
          previewData.data,
          headerLabel.rowIndex,
          headerLabel.colIndex,
          maxRowScan,
          mergedRanges
        );
        if (columnValues.length > 0) {
          columnValues.forEach((value, idx) => {
            const valueRow = headerLabel.rowIndex + 1 + idx;
            const valueKey = `${valueRow},${headerLabel.colIndex}`;
            values[valueKey] = {
              value,
              labelKey: headerLabel.cellKey,
              method: `table-column-${idx + 1}`,
              position: { row: valueRow, col: headerLabel.colIndex },
              quality: getValueQuality(value),
              extractionType: 'table'
            };
            mappings.push({
              label: headerLabel.targetLabel,
              detectedLabel: headerLabel.detectedText,
              value,
              similarity: headerLabel.similarity,
              labelPosition: { row: headerLabel.rowIndex + 1, col: headerLabel.colIndex + 1 },
              valuePosition: { row: valueRow + 1, col: headerLabel.colIndex + 1 },
              method: `table-column-${idx + 1}`,
              quality: getValueQuality(value),
              extractionType: 'table'
            });
          });
          tableExtractions.push({
            label: headerLabel.targetLabel,
            headerPosition: { row: headerLabel.position.row, col: headerLabel.position.col },
            valuesCount: columnValues.length,
            values: columnValues
          });
        }
      });
    }
    
    // Phase 4: Handle isolated labels (key-value pairs)
    const headerRowIndices = new Set(headerRows.map(hr => hr.rowIndex));
    const isolatedLabels = labelMatches.filter(match => !headerRowIndices.has(match.rowIndex));
    
    console.log(`🔍 Processing ${isolatedLabels.length} isolated labels`);
    
    isolatedLabels.forEach(isolatedLabel => {
      // Look for value using enhanced algorithm with merged cell support
      const valuePosition = findValueForLabel(
        previewData.data, 
        isolatedLabel.rowIndex, 
        isolatedLabel.colIndex, 
        maxRowScan,
        mergedRanges
      );
      
      if (valuePosition) {
        const valueKey = `${valuePosition.row},${valuePosition.col}`;
        const extractedValue = getCellValue(previewData.data, valuePosition.row, valuePosition.col);
        
        if (extractedValue && isValidValue(extractedValue, isolatedLabel.detectedText)) {
          values[valueKey] = {
            value: extractedValue,
            labelKey: isolatedLabel.cellKey,
            method: valuePosition.method,
            position: valuePosition,
            quality: valuePosition.quality || getValueQuality(extractedValue),
            extractionType: 'isolated'
          };
          
          mappings.push({
            label: isolatedLabel.targetLabel,
            detectedLabel: isolatedLabel.detectedText,
            value: extractedValue,
            similarity: isolatedLabel.similarity,
            labelPosition: { row: isolatedLabel.rowIndex + 1, col: isolatedLabel.colIndex + 1 },
            valuePosition: { row: valuePosition.row + 1, col: valuePosition.col + 1 },
            method: valuePosition.method,
            quality: valuePosition.quality || getValueQuality(extractedValue),
            extractionType: 'isolated'
          });
          
          console.log(`✅ Enhanced isolated mapping: "${isolatedLabel.targetLabel}" → "${extractedValue}" (${valuePosition.method})`);
        }
      } else {
        console.log(`❌ No value found for isolated label: "${isolatedLabel.targetLabel}"`);
      }
    });
    
    // Filter out low-quality mappings
    const filteredMappings = mappings.filter(mapping => (mapping.quality || 0) >= 1.0);
    // Deduplicate: keep only the rightmost label match per field
    const uniqueMap = {};
    filteredMappings.forEach(m => {
      const lbl = m.label;
      // Keep the mapping whose extracted value is farthest right
      if (!uniqueMap[lbl] || m.valuePosition.col > uniqueMap[lbl].valuePosition.col) {
        uniqueMap[lbl] = m;
      }
    });
    const uniqueMappings = Object.values(uniqueMap);
    
    const analysis = {
      totalLabels: targetLabels.length,
      foundLabels: labelMatches.length,
      headerRows: headerRows.length,
      tableExtractions: tableExtractions.length,
      isolatedMappings: isolatedLabels.length,
      totalMappings: mappings.length,
      qualityMappings: uniqueMappings.length,
      extractionMethod: 'smart-analysis'
    };
    
    console.log('📊 Smart analysis complete:', analysis);
    
    return { 
      labels, 
      values, 
      mappings: uniqueMappings,
      headerRows,
      tableExtractions,
      isolatedLabels,
      analysis,
      rawMappings: mappings
    };
  };

  // Extract column data below a header cell (enhanced with merged cell awareness)
  const extractColumnData = (data, headerRow, colIndex, maxRows, mergedRanges = []) => {
    const values = [];
    
    for (let rowOffset = 1; rowOffset <= maxRows && headerRow + rowOffset < data.length; rowOffset++) {
      const currentRow = headerRow + rowOffset;
      const cellValue = getCellValue(data, currentRow, colIndex);
      
      if (cellValue && String(cellValue).trim()) {
        const valueStr = String(cellValue).trim();
        
        // Check if this cell is part of a merged range
        const mergeRange = findMergeRange(currentRow, colIndex, mergedRanges);
        if (mergeRange) {
          console.log(`📊 Column value at R${currentRow + 1}C${colIndex + 1} is in merged range`);
          // Use the merged cell's value if different from what we already have
          const mergedValue = getCellValue(data, mergeRange.s.r, mergeRange.s.c);
          if (mergedValue && mergedValue !== valueStr) {
            values.push(String(mergedValue).trim());
          } else {
            values.push(valueStr);
          }
        } else {
          // Stop if we hit another header row (multiple labels in the row)
          let labelsInRow = 0;
          const row = data[currentRow];
          if (row) {
            row.forEach(cell => {
              if (cell && String(cell).trim().length > 0) {
                // Check if this looks like a label (short, uppercase, etc.)
                if (String(cell).trim().length < 20 && /^[A-Z\s]+$/.test(String(cell).trim())) {
                  labelsInRow++;
                }
              }
            });
          }
          
          if (labelsInRow >= 3) {
            console.log(`🛑 Stopping column extraction at row ${currentRow + 1} - detected another header`);
            break;
          }
          
          values.push(valueStr);
        }
      } else {
        // Stop on first empty cell to avoid capturing unrelated data
        break;
      }
    }
    
    return values;
  };

  // Validate if a value is acceptable (not empty, not same as label, not just whitespace)
  const isValidValue = (value, labelText) => {
    if (!value) return false;
    
    const valueStr = String(value).trim();
    const labelStr = String(labelText).trim();
    
    // Reject empty or whitespace-only values
    if (valueStr.length === 0) return false;
    
    // Reject if value is exactly the same as label (case-insensitive)
    if (valueStr.toLowerCase() === labelStr.toLowerCase()) return false;
    
    // Reject if value is just punctuation or special characters
    if (/^[^\w\d]+$/.test(valueStr)) return false;
    
    // Reject obvious placeholder text
    const placeholders = ['n/a', 'na', 'null', 'none', 'empty', 'blank', 'tbd', 'todo'];
    if (placeholders.includes(valueStr.toLowerCase())) return false;
    
    return true;
  };

  // Calculate value quality score (prefer numbers, dates, meaningful text)
  const getValueQuality = (value) => {
    if (!value) return 0;
    
    const valueStr = String(value).trim();
    let quality = 1; // Base quality
    
    // Prefer numeric values
    if (/^\d+(\.\d+)?$/.test(valueStr)) {
      quality += 3; // Pure numbers get high score
    } else if (/^\d+[\s\w]*$/.test(valueStr)) {
      quality += 2; // Numbers with units
    }
    
    // Prefer date-like patterns
    if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(valueStr)) {
      quality += 2;
    }
    
    // Prefer meaningful text (longer than 3 characters)
    if (valueStr.length > 3 && /[a-zA-Z]/.test(valueStr)) {
      quality += 1;
    }
    
    // Penalty for very short text (likely labels or headers)
    if (valueStr.length <= 2) {
      quality -= 1;
    }
    
    // Penalty for all uppercase (likely labels)
    if (valueStr === valueStr.toUpperCase() && /[a-zA-Z]/.test(valueStr)) {
      quality -= 0.5;
    }
    
    return Math.max(0, quality);
  };

  // Advanced value extraction with merged cell support (inspired by extractBHA.js)
  const findValueForLabel = (data, labelRow, labelCol, maxRowScan, mergedRanges = []) => {
    const labelText = data[labelRow][labelCol];
    const valueCandidates = [];
    
    console.log(`🔍 Finding value for label "${labelText}" at R${labelRow + 1}C${labelCol + 1}`);
    
    // Check if the label cell is in a merged range
    const labelMergeRange = findMergeRange(labelRow, labelCol, mergedRanges);
    
    if (labelMergeRange) {
      console.log(`📊 Label is in merged range: R${labelMergeRange.s.r + 1}C${labelMergeRange.s.c + 1}:R${labelMergeRange.e.r + 1}C${labelMergeRange.e.c + 1}`);
      
      // Strategy 1: Check merged cell itself for value (if different from label)
      const mergedValue = getCellValue(data, labelMergeRange.s.r, labelMergeRange.s.c);
      if (mergedValue && mergedValue !== labelText && isValidValue(mergedValue, labelText)) {
        valueCandidates.push({
          row: labelMergeRange.s.r,
          col: labelMergeRange.s.c,
          method: 'merged-cell-value',
          value: mergedValue,
          quality: getValueQuality(mergedValue),
          distance: 0
        });
      }
      
      // Strategy 2: Check below the merged range
      const belowMerged = getCellValue(data, labelMergeRange.e.r + 1, labelMergeRange.s.c);
      if (belowMerged && isValidValue(belowMerged, labelText)) {
        valueCandidates.push({
          row: labelMergeRange.e.r + 1,
          col: labelMergeRange.s.c,
          method: 'below-merged',
          value: belowMerged,
          quality: getValueQuality(belowMerged),
          distance: 1
        });
      }
      
      // Strategy 3: Check to the right of the merged range
      const rightOfMerged = getCellValue(data, labelMergeRange.s.r, labelMergeRange.e.c + 1);
      if (rightOfMerged && isValidValue(rightOfMerged, labelText)) {
        valueCandidates.push({
          row: labelMergeRange.s.r,
          col: labelMergeRange.e.c + 1,
          method: 'right-of-merged',
          value: rightOfMerged,
          quality: getValueQuality(rightOfMerged),
          distance: 1
        });
      }
      
      // Strategy 4: Check diagonal from merged range
      const diagonalFromMerged = getCellValue(data, labelMergeRange.e.r + 1, labelMergeRange.e.c + 1);
      if (diagonalFromMerged && isValidValue(diagonalFromMerged, labelText)) {
        valueCandidates.push({
          row: labelMergeRange.e.r + 1,
          col: labelMergeRange.e.c + 1,
          method: 'diagonal-from-merged',
          value: diagonalFromMerged,
          quality: getValueQuality(diagonalFromMerged),
          distance: 1.4
        });
      }
    } else {
      // Regular cell (not merged) - use standard strategies
      
      // Strategy 1: Scan to the right
      for (let colOffset = 1; colOffset <= 5 && labelCol + colOffset < (data[labelRow]?.length || 0); colOffset++) {
        const rightValue = getCellValue(data, labelRow, labelCol + colOffset);
        
        if (rightValue && isValidValue(rightValue, labelText)) {
          valueCandidates.push({
            row: labelRow,
            col: labelCol + colOffset,
            method: `right-${colOffset}`,
            value: rightValue,
            quality: getValueQuality(rightValue),
            distance: colOffset
          });
          
          // Prefer high-quality close values
          if (getValueQuality(rightValue) >= 3 && colOffset <= 2) {
            break;
          }
        }
      }
      
      // Strategy 2: Scan below
      for (let rowOffset = 1; rowOffset <= maxRowScan && labelRow + rowOffset < data.length; rowOffset++) {
        const belowValue = getCellValue(data, labelRow + rowOffset, labelCol);
        
        if (belowValue && isValidValue(belowValue, labelText)) {
          valueCandidates.push({
            row: labelRow + rowOffset,
            col: labelCol,
            method: `below-${rowOffset}`,
            value: belowValue,
            quality: getValueQuality(belowValue),
            distance: rowOffset
          });
          
          // Prefer high-quality close values
          if (getValueQuality(belowValue) >= 3 && rowOffset <= 2) {
            break;
          }
        }
      }
      
      // Strategy 3: Diagonal scan
      for (let offset = 1; offset <= Math.min(3, maxRowScan); offset++) {
        const diagonalValue = getCellValue(data, labelRow + offset, labelCol + offset);
        
        if (diagonalValue && isValidValue(diagonalValue, labelText)) {
          valueCandidates.push({
            row: labelRow + offset,
            col: labelCol + offset,
            method: `diagonal-${offset}`,
            value: diagonalValue,
            quality: getValueQuality(diagonalValue),
            distance: offset * 1.4
          });
        }
      }
    }
    
    // Strategy 5: Extended search for wide layouts
    if (valueCandidates.length === 0) {
      for (let colOffset = 6; colOffset <= 10 && labelCol + colOffset < (data[labelRow]?.length || 0); colOffset++) {
        const extendedValue = getCellValue(data, labelRow, labelCol + colOffset);
        
        if (extendedValue && isValidValue(extendedValue, labelText)) {
          valueCandidates.push({
            row: labelRow,
            col: labelCol + colOffset,
            method: `extended-right-${colOffset}`,
            value: extendedValue,
            quality: getValueQuality(extendedValue) * 0.8, // Distance penalty
            distance: colOffset
          });
        }
      }
    }
    
    // Prepare candidate pool for selection
    if (valueCandidates.length === 0) {
      console.log(`❌ No valid value found for "${labelText}"`);
      return null;
    }
    // Exclude label cell itself
    const withoutLabelCell = valueCandidates.filter(c => !(c.row === labelRow && c.col === labelCol));
    // Prefer text containing values, fallback to numeric
    const textPool = withoutLabelCell.filter(c => /[A-Za-z]/.test(String(c.value)));
    const numericPool = withoutLabelCell.filter(c => !isNaN(Number(c.value)));
    // For numeric fields, pick numbers first; fallback to text otherwise
    const candidatePool = numericPool.length > 0
      ? numericPool
      : textPool.length > 0
        ? textPool
        : withoutLabelCell;
    // Sort pool by quality (desc) then distance (asc)
    candidatePool.sort((a, b) => {
      const qualityDiff = b.quality - a.quality;
      if (Math.abs(qualityDiff) > 0.5) {
        return qualityDiff;
      }
      return a.distance - b.distance;
    });
    const bestValCandidate = candidatePool[0];
    console.log(`🎯 Found value for "${labelText}": "${bestValCandidate.value}" (quality: ${bestValCandidate.quality}, method: ${bestValCandidate.method})`);
    
    return {
      row: bestValCandidate.row,
      col: bestValCandidate.col,
      method: bestValCandidate.method,
      quality: bestValCandidate.quality
    };
  };

  // Helper function to find merge range for a cell
  const findMergeRange = (row, col, mergedRanges) => {
    return mergedRanges.find(range => 
      row >= range.s.r && row <= range.e.r && 
      col >= range.s.c && col <= range.e.c
    );
  };

  // Helper function to safely get cell value
  const getCellValue = (data, row, col) => {
    if (row < 0 || row >= data.length) return null;
    if (col < 0 || col >= (data[row]?.length || 0)) return null;
    return data[row][col];
  };

  // Direct extraction using extractBHA.js algorithm
  const extractValueByLabel = (data, targetLabel, fuzzyThreshold = 0.85, mergedRanges = []) => {
    console.log(`🎯 Direct extraction for label: "${targetLabel}"`);
    let lastExtraction = { label: targetLabel, detectedLabel: null, value: null, method: 'not-found', similarity: 0, labelPosition: null, valuePosition: null, quality: 0 };
    for (let r = 0; r < data.length; r++) {
      for (let c = 0; c < (data[r]?.length || 0); c++) {
        const cellValue = getCellValue(data, r, c);
        if (!cellValue || typeof cellValue !== 'string') continue;
        const similarity = calculateSimilarity(cellValue, targetLabel);
        if (similarity >= fuzzyThreshold) {
          console.log(`✅ Found label match: "${cellValue}" → "${targetLabel}" (${Math.round(similarity * 100)}%) at R${r + 1}C${c + 1}`);
          const mergeRange = findMergeRange(r, c, mergedRanges);
          let extraction = null;
          if (mergeRange) {
            const mergedValue = getCellValue(data, mergeRange.s.r, mergeRange.s.c);
            if (mergedValue && mergedValue !== cellValue && isValidValue(mergedValue, cellValue)) {
              extraction = { label: targetLabel, detectedLabel: cellValue, value: mergedValue, method: 'merged-cell-value', similarity, labelPosition: { row: r + 1, col: c + 1 }, valuePosition: { row: mergeRange.s.r + 1, col: mergeRange.s.c + 1 }, quality: getValueQuality(mergedValue) };
            }
            const belowMerged = getCellValue(data, mergeRange.e.r + 1, mergeRange.s.c);
            if (!extraction && belowMerged && isValidValue(belowMerged, cellValue)) {
              extraction = { label: targetLabel, detectedLabel: cellValue, value: belowMerged, method: 'below-merged', similarity, labelPosition: { row: r + 1, col: c + 1 }, valuePosition: { row: mergeRange.e.r + 2, col: mergeRange.s.c + 1 }, quality: getValueQuality(belowMerged) };
            }
            const rightOfMerged = getCellValue(data, mergeRange.s.r, mergeRange.e.c + 1);
            if (!extraction && rightOfMerged && isValidValue(rightOfMerged, cellValue)) {
              extraction = { label: targetLabel, detectedLabel: cellValue, value: rightOfMerged, method: 'right-of-merged', similarity, labelPosition: { row: r + 1, col: c + 1 }, valuePosition: { row: mergeRange.s.r + 1, col: mergeRange.e.c + 2 }, quality: getValueQuality(rightOfMerged) };
            }
          } else {
            const rightValue = getCellValue(data, r, c + 1);
            if (rightValue && isValidValue(rightValue, cellValue)) {
              extraction = { label: targetLabel, detectedLabel: cellValue, value: rightValue, method: 'right', similarity, labelPosition: { row: r + 1, col: c + 1 }, valuePosition: { row: r + 1, col: c + 2 }, quality: getValueQuality(rightValue) };
            }
            const belowValue = getCellValue(data, r + 1, c);
            if (!extraction && belowValue && isValidValue(belowValue, cellValue)) {
              extraction = { label: targetLabel, detectedLabel: cellValue, value: belowValue, method: 'below', similarity, labelPosition: { row: r + 1, col: c + 1 }, valuePosition: { row: r + 2, col: c + 1 }, quality: getValueQuality(belowValue) };
            }
          }
          if (extraction) {
            // Skip if extracted value is actually another label
            const valStr = String(extraction.value).trim();
            const isLabelMatch = currentTargetLabels.some(lbl => lbl.trim().toLowerCase() === valStr.toLowerCase() && lbl !== targetLabel);
            if (!isLabelMatch) {
              lastExtraction = extraction;
            } else {
              console.log(`⏭️ Skipping value match for "${targetLabel}" as "${valStr}" is another label`);
            }
          }
        }
      }
    }
    return lastExtraction;
  };

  // Update live preview when options change
  const updateLivePreview = () => {
    if (sheetPreview) {
      const analysis = analyzePreviewData(sheetPreview, currentTargetLabels, extractionOptions);
      setLivePreview(analysis);
      setHighlightedCells({
        labels: analysis.labels,
        values: analysis.values
      });
    }
  };

  // Load templates from backend
  const loadTemplates = async () => {
    try {
      const res = await fetch('/api/excel-mapping/templates');
      const result = await res.json();
      if (result.success) setTemplates(result.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Save current mappings as template
  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    const baseMappings = extractionResults?.results?.mappings || {};
    const mappingsToSave = { ...baseMappings, ...manualMappings };
    try {
      const res = await fetch('/api/excel-mapping/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTemplateName.trim(), mappings: mappingsToSave })
      });
      const result = await res.json();
      if (result.success) {
        setNewTemplateName('');
        loadTemplates();
      } else {
        alert(result.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('Save template error:', err);
      alert('Failed to save template');
    }
  };

  // Delete a saved template
  const handleDeleteTemplate = async (name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      const res = await fetch(`/api/excel-mapping/templates/${encodeURIComponent(name)}`, { method: 'DELETE' });
      const result = await res.json();
      if (result.success) {
        loadTemplates();
      } else {
        alert(result.error || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Delete template error:', err);
      alert('Failed to delete template');
    }
  };

  // Apply a saved template mappings
  const applyTemplate = (templateMappings) => {
    setManualMappings(templateMappings);
  };

  // Upload Step Component
  const UploadStep = () => (
    <div className="excel-step upload-step">
      <div className="step-header">
        <h3>📤 {uploadedFile ? 'Processing Excel File' : 'Upload Excel File'}</h3>
        <p>{uploadedFile ? `Processing: ${uploadedFile.name}` : 'Select an Excel file (.xlsx or .xls) containing your data'}</p>
      </div>

      {uploadedFile ? (
        // Show file info when processing
        <div className="file-processing-info">
          <div className="processing-file">
            <div className="file-icon">📊</div>
            <div className="file-details">
              <h4>{uploadedFile.name}</h4>
              <p>File size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              <p>Type: {uploadedFile.type || 'Excel file'}</p>
            </div>
          </div>
          {isProcessing && (
            <div className="processing-status">
              <div className="processing-spinner"></div>
              <p>Analyzing Excel file structure...</p>
            </div>
          )}
        </div>
      ) : (
        // Show upload zone when no file
        <div 
          className={`file-drop-zone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="drop-zone-content">
            <div className="upload-icon">📊</div>
            <h4>Drop Excel file here</h4>
            <p>or</p>
            <label className="file-input-label">
              <input
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
              <span className="btn btn-primary">Choose File</span>
            </label>
            <div className="file-requirements">
              <p>Supported formats: .xlsx, .xls</p>
              <p>Maximum file size: 10MB</p>
            </div>
          </div>
        </div>
      )}

      <div className="target-labels-info">
        <h5>🎯 Target Labels ({currentTargetLabels.length})</h5>
        <div className="labels-preview">
          {currentTargetLabels.slice(0, 8).map((label, index) => (
            <span key={index} className="label-chip">{label}</span>
          ))}
          {currentTargetLabels.length > 8 && (
            <span className="more-labels">+{currentTargetLabels.length - 8} more</span>
          )}
        </div>
      </div>
    </div>
  );

  // Sheet Selection Step Component
  const SheetSelectionStep = () => (
    <div className="excel-step sheets-step">
      <div className="step-header">
        <h3>📋 Select Worksheet</h3>
        <p>Choose the worksheet containing your data</p>
        <div className="file-info">
          <span className="file-name">{uploadedFile?.name}</span>
          <span className="sheet-count">{availableSheets.length} sheet{availableSheets.length !== 1 ? 's' : ''} found</span>
        </div>
      </div>

      <div className="sheets-list">
        {availableSheets.map((sheet, index) => {
          const recommendation = getSheetRecommendation(sheet);
          return (
            <div
              key={index}
              className={`sheet-option ${recommendation.recommended ? 'recommended' : ''}`}
              onClick={() => handleSheetSelection(sheet.name)}
            >
              <div className="sheet-header">
                <div className="sheet-main-info">
                  <span className="sheet-icon">
                    {recommendation.recommended ? '⭐' : '📄'}
                  </span>
                  <span className="sheet-name">{sheet.name}</span>
                  {recommendation.recommended && (
                    <span className="recommended-badge">Recommended</span>
                  )}
                </div>
                <div className="sheet-meta">
                  <span className="sheet-size">{sheet.rowCount} × {sheet.columnCount}</span>
                </div>
              </div>
              
              <div className="sheet-details">
                <div className="sheet-reason">{recommendation.reason}</div>
              </div>
              
              <div className="sheet-action">
                <button className="select-sheet-btn">
                  <span>Select Sheet</span>
                  <span className="action-icon">→</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={resetToUpload}>
          ← Back to Upload
        </button>
      </div>
    </div>
  );

  // Scan Results Step Component
  const ScanResultsStep = () => {
    if (!livePreview || !sheetPreview) return null;

    // Group found and not found fields by parent/child
    const foundLabels = new Set(livePreview.mappings.map(mapping => mapping.label));
    const isDualMode = parentLabels.length > 0 && childLabels.length > 0;
    
    const foundParentFields = [];
    const notFoundParentFields = [];
    const foundChildFields = [];
    const notFoundChildFields = [];
    const foundGeneralFields = [];
    const notFoundGeneralFields = [];

    if (isDualMode) {
      // Dual table mode - separate by parent/child
      parentLabels.forEach(label => {
        if (foundLabels.has(label)) {
          const mapping = livePreview.mappings.find(m => m.label === label);
          foundParentFields.push({ label, mapping });
        } else {
          notFoundParentFields.push(label);
        }
      });

      childLabels.forEach(label => {
        if (foundLabels.has(label)) {
          const mapping = livePreview.mappings.find(m => m.label === label);
          foundChildFields.push({ label, mapping });
        } else {
          notFoundChildFields.push(label);
        }
      });
    } else {
      // Single table mode
      currentTargetLabels.forEach(label => {
        if (foundLabels.has(label)) {
          const mapping = livePreview.mappings.find(m => m.label === label);
          foundGeneralFields.push({ label, mapping });
        } else {
          notFoundGeneralFields.push(label);
        }
      });
    }

    const renderFieldGroup = (title, foundFields, notFoundFields, groupIcon) => (
      <div className="field-group">
        <h5>
          {groupIcon} {title} ({foundFields.length + notFoundFields.length} fields)
        </h5>
        
        {foundFields.length > 0 && (
          <div className="found-fields-section">
            <h6 className="found-header">✅ Found ({foundFields.length})</h6>
            <div className="found-fields-list">
              {foundFields.map(({ label, mapping }, index) => (
                <div key={index} className="found-field-item single-line">
                  <span className="field-label">{label}</span>
                  <span className="separator">→</span>
                  <span className="field-value">{mapping.value}</span>
                  <span className="similarity">{Math.round(mapping.similarity * 100)}%</span>
                  <span className="method">{mapping.method}</span>
                  <span className="position">R{mapping.labelPosition.row}C{mapping.labelPosition.col}→R{mapping.valuePosition.row}C{mapping.valuePosition.col}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {notFoundFields.length > 0 && (
          <div className="not-found-fields-section">
            <h6 className="not-found-header">❌ Not Found ({notFoundFields.length})</h6>
            <div className="not-found-fields-list">
              {notFoundFields.map((label, index) => (
                <div key={index} className="not-found-field-item">
                  <span className="field-label">{label}</span>
                  <span className="field-status">No match found</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div className="excel-step scan-results-step">
        <div className="step-header">
          <h3>🔍 Scan Results</h3>
          <p>Field mapping results from Excel sheet analysis</p>
          <div className="selected-sheet-info">
            <span className="sheet-icon">📄</span>
            <span className="sheet-name">{selectedSheet}</span>
            <span className="sheet-size">{sheetPreview.totalRows} × {sheetPreview.totalCols}</span>
          </div>
        </div>

        {/* Overall Statistics */}
        <div className="scan-summary">
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{livePreview.mappings.length}</span>
              <span className="stat-label">Fields Found</span>
            </div>
            <div className="stat">
              <span className="stat-value">{currentTargetLabels.length - livePreview.mappings.length}</span>
              <span className="stat-label">Fields Missing</span>
            </div>
            <div className="stat">
              <span className="stat-value">{currentTargetLabels.length}</span>
              <span className="stat-label">Total Fields</span>
            </div>
            <div className="stat">
              <span className="stat-value">{Math.round((livePreview.mappings.length / currentTargetLabels.length) * 100)}%</span>
              <span className="stat-label">Success Rate</span>
            </div>
          </div>
        </div>

        {/* Field Groups */}
        <div className="field-groups">
          {isDualMode ? (
            <>
              {renderFieldGroup(
                "Parent Table Fields", 
                foundParentFields, 
                notFoundParentFields, 
                "👨‍👩‍👧‍👦"
              )}
              {renderFieldGroup(
                "Child Table Fields", 
                foundChildFields, 
                notFoundChildFields, 
                "👶"
              )}
            </>
          ) : (
            renderFieldGroup(
              "Form Fields", 
              foundGeneralFields, 
              notFoundGeneralFields, 
              "📝"
            )
          )}
        </div>

        {/* Excel Preview with Highlighting */}
        <div className="highlighted-preview">
          <h5>📊 Excel Preview with Highlights</h5>
          <div className="preview-legend">
            <div className="legend-item">
              <span className="legend-color label-highlight"></span>
              <span className="legend-text">Found Labels</span>
            </div>
            <div className="legend-item">
              <span className="legend-color value-highlight"></span>
              <span className="legend-text">Extracted Values</span>
            </div>
          </div>
          
          <div className="preview-table">
            <table>
              <tbody>
                {sheetPreview.data.map((row, rowIndex) => {
                  const isHeaderRow = livePreview && livePreview.headerRows && livePreview.headerRows.length > 0 && rowIndex === livePreview.headerRows[0].rowIndex;
                  return (
                    <tr key={rowIndex} className={isHeaderRow ? 'header-row-pink' : ''}>
                      <td className="row-number">{rowIndex + 1}</td>
                      {row.map((cell, cellIndex) => {
                        const cellKey = `${rowIndex},${cellIndex}`;
                        const isLabel = highlightedCells.labels[cellKey];
                        const isValue = highlightedCells.values[cellKey];
                        
                        let cellClass = 'preview-cell';
                        let cellTitle = '';
                        
                        if (isLabel) {
                          cellClass += ' highlighted-label';
                          cellTitle = `Label: "${isLabel.targetLabel}" (${Math.round(isLabel.similarity * 100)}% match)`;
                        }
                        if (isValue) {
                          cellClass += ' highlighted-value';
                          const labelInfo = highlightedCells.labels[isValue.labelKey];
                          cellTitle += (cellTitle ? ' | ' : '') + `Value for: "${labelInfo?.targetLabel}" (${isValue.method})`;
                        }
                        
                        return (
                          <td key={cellIndex} className={cellClass} title={cellTitle}>
                            {cell || <em>empty</em>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {sheetPreview.totalRows > sheetPreview.data.length && (
            <div className="preview-info">
              Showing first {sheetPreview.data.length} of {sheetPreview.totalRows} rows
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="step-actions">
          <button className="btn btn-secondary" onClick={() => setStep('sheets')}>
            ← Change Sheet
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleExtractData}
            disabled={isProcessing || livePreview.mappings.length === 0}
          >
            {isProcessing ? 'Processing...' : `Extract Data (${livePreview.mappings.length} fields)`}
          </button>
        </div>
      </div>
    );
  };

  // Extraction Options Step Component
  const FieldMappingStep = () => (
    <div className="excel-step extract-step">
      <div className="step-header">
        <h3>🔍 Extract Data</h3>
        <p>Configure extraction options and preview your data</p>
        <div className="selected-sheet-info">
          <span className="sheet-icon">📄</span>
          <span className="sheet-name">{selectedSheet}</span>
          {sheetPreview && (
            <span className="sheet-size">{sheetPreview.totalRows} × {sheetPreview.totalCols}</span>
          )}
        </div>
      </div>

      <div className="extraction-options">
        <h5>⚙️ Extraction Options</h5>
        <div className="options-grid">
          <div className="option-group">
            <label>Fuzzy Match Threshold</label>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={extractionOptions.fuzzyThreshold}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                fuzzyThreshold: parseFloat(e.target.value)
              }))}
            />
            <span className="option-value">{Math.round(extractionOptions.fuzzyThreshold * 100)}%</span>
          </div>

          <div className="option-group">
            <label>Max Row Scan</label>
            <input
              type="number"
              min="1"
              max="20"
              value={extractionOptions.maxRowScan}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                maxRowScan: parseInt(e.target.value)
              }))}
            />
            <span className="option-hint">Rows to scan below label</span>
          </div>

          <div className="option-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={extractionOptions.enableMultiRowHeaders}
                onChange={(e) => setExtractionOptions(prev => ({
                  ...prev,
                  enableMultiRowHeaders: e.target.checked
                }))}
              />
              Enable Multi-Row Headers
            </label>
            <span className="option-hint">Combine consecutive rows for labels</span>
          </div>

          <div className="option-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={extractionOptions.caseSensitive}
                onChange={(e) => setExtractionOptions(prev => ({
                  ...prev,
                  caseSensitive: e.target.checked
                }))}
              />
              Case Sensitive Matching
            </label>
          </div>
        </div>
      </div>

      {sheetPreview && (
        <>
          <div className="full-preview-toggle" style={{ marginBottom: '8px' }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleFullPreview}
              disabled={isProcessing}
            >
              Full Preview
            </button>
          </div>
          <div className="sheet-preview">
            <h5>🧠 Smart Detection Preview</h5>
            
            {/* Target Labels Debug */}
            <div className="target-labels-debug">
              <h6>🎯 Target Labels Being Searched ({currentTargetLabels.length})</h6>
              {targetLabels.length > 15 && (
                <div className="dual-table-notice">
                  <span className="notice-icon">📊</span>
                  <span className="notice-text">
                    Dual Table Mode: Searching columns from both parent and child tables
                  </span>
                </div>
              )}
              <div className="debug-labels-grid">
                {currentTargetLabels.map((label, index) => {
                  const isFound = livePreview && livePreview.mappings && 
                    livePreview.mappings.some(mapping => mapping.label === label);
                  
                  return (
                    <div key={index} className={`debug-label-item ${isFound ? 'found' : 'not-found'}`}>
                      <span className="label-index">{index + 1}</span>
                      <span className="label-text">{label}</span>
                      <span className="label-status">{isFound ? '✅' : '❌'}</span>
                    </div>
                  );
                })}
              </div>
              <div className="debug-summary">
                <span className="found-count">
                  Found: {livePreview ? livePreview.mappings.length : 0}/{currentTargetLabels.length}
                </span>
                <span className="success-rate">
                  ({livePreview ? Math.round((livePreview.mappings.length / currentTargetLabels.length) * 100) : 0}% success rate)
                </span>
              </div>
            </div>

            {/* Analysis Summary */}
            {livePreview && livePreview.analysis && (
              <div className="smart-analysis-summary">
                <div className="extraction-method-indicator">
                  <span className="method-icon">
                    {livePreview.analysis.extractionMethod === 'direct' ? '🎯' : '🧠'}
                  </span>
                  <span className="method-text">
                    {livePreview.analysis.extractionMethod === 'direct' 
                      ? 'Direct Extraction (extractBHA.js style)' 
                      : 'Smart Analysis (multi-phase detection)'
                    }
                  </span>
                  <span className="method-badge">
                    {livePreview.analysis.extractionMethod === 'direct' ? 'PRIMARY' : 'FALLBACK'}
                  </span>
                </div>
                
                <div className="analysis-stats">
                  <div className="analysis-stat">
                    <span className="stat-icon">🏷️</span>
                    <span className="stat-value">{livePreview.analysis.foundLabels}</span>
                    <span className="stat-label">Labels Found</span>
                  </div>
                  <div className="analysis-stat">
                    <span className="stat-icon">📊</span>
                    <span className="stat-value">{livePreview.analysis.headerRows}</span>
                    <span className="stat-label">Header Rows</span>
                  </div>
                  <div className="analysis-stat">
                    <span className="stat-icon">📋</span>
                    <span className="stat-value">{livePreview.analysis.tableExtractions}</span>
                    <span className="stat-label">Table Columns</span>
                  </div>
                  <div className="analysis-stat">
                    <span className="stat-icon">🔍</span>
                    <span className="stat-value">{livePreview.analysis.isolatedMappings}</span>
                    <span className="stat-label">Isolated Pairs</span>
                  </div>
                  <div className="analysis-stat">
                    <span className="stat-icon">✅</span>
                    <span className="stat-value">{livePreview.analysis.qualityMappings}</span>
                    <span className="stat-label">Quality Mappings</span>
                  </div>
                </div>
                
                {livePreview.headerRows && livePreview.headerRows.length > 0 && (
                  <div className="header-analysis">
                    <h6>📊 Table Headers Detected:</h6>
                    <div className="header-list">
                      {livePreview.headerRows.map((header, index) => (
                        <div key={index} className="header-item">
                          Row {header.rowIndex + 1}: {header.labelCount} labels 
                          ({Math.round(header.confidence * 100)}% coverage)
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="preview-table">
              <table>
                <tbody>
                  {sheetPreview.data.map((row, rowIndex) => {
                    const isHeaderRow = livePreview && livePreview.headerRows && livePreview.headerRows.length > 0 && rowIndex === livePreview.headerRows[0].rowIndex;
                    return (
                      <tr key={rowIndex} className={isHeaderRow ? 'header-row-pink' : ''}>
                        <td className="row-number">{rowIndex + 1}</td>
                        {row.map((cell, cellIndex) => {
                          const cellKey = `${rowIndex},${cellIndex}`;
                          const isLabel = highlightedCells.labels[cellKey];
                          const isValue = highlightedCells.values[cellKey];
                          
                          let cellClass = 'preview-cell';
                          let cellTitle = '';
                          let cellIcon = '';
                          let qualityBadge = '';
                          
                          if (isLabel) {
                            if (isHeaderRow) {
                              cellClass += ' header-label';
                              cellTitle = `Header Label: "${isLabel.targetLabel}" (${Math.round(isLabel.similarity * 100)}% match)`;
                              cellIcon = '📊';
                            } else {
                              cellClass += ' isolated-label';
                              cellTitle = `Isolated Label: "${isLabel.targetLabel}" (${Math.round(isLabel.similarity * 100)}% match)`;
                              cellIcon = '🏷️';
                            }
                          } else if (isValue) {
                            const extractionType = isValue.extractionType;
                            
                            if (extractionType === 'table') {
                              cellClass += ' table-value';
                              cellTitle = `Table Data: "${highlightedCells.labels[isValue.labelKey]?.targetLabel}" (${isValue.method})`;
                              cellIcon = '📋';
                            } else {
                              cellClass += ' isolated-value';
                              cellTitle = `Isolated Value: "${highlightedCells.labels[isValue.labelKey]?.targetLabel}" (${isValue.method})`;
                              cellIcon = '📄';
                            }
                            
                            // Add quality badge
                            const quality = isValue.quality || 0;
                            if (quality >= 3) {
                              qualityBadge = '🟢';
                            } else if (quality >= 2) {
                              qualityBadge = '🟡';
                            } else if (quality >= 1) {
                              qualityBadge = '🟠';
                            } else {
                              qualityBadge = '🔴';
                            }
                            
                            cellTitle += ` | Quality: ${quality.toFixed(1)} ${qualityBadge}`;
                          }
                          
                          return (
                            <td
                              key={cellIndex}
                              className={`${cellClass}${mappingMode ? ' manual-mapping-active' : ''}`}
                              title={cellTitle}
                              onClick={() => handleCellClick(rowIndex, cellIndex)}
                            >
                              <div className="cell-content">
                                {cellIcon && <span className="cell-icon">{cellIcon}</span>}
                                <span className="cell-text">{cell || <em>empty</em>}</span>
                                {qualityBadge && <span className="quality-badge">{qualityBadge}</span>}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="smart-preview-legend">
              <h6>🔍 Smart Detection Legend:</h6>
              <div className="legend-grid">
                <div className="legend-item">
                  <span className="legend-icon">📊</span>
                  <span className="legend-text">Header Label (in table header row)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">🏷️</span>
                  <span className="legend-text">Isolated Label (key-value pair)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">📋</span>
                  <span className="legend-text">Table Data (column values)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">📄</span>
                  <span className="legend-text">Isolated Value (right of label)</span>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">🟢🟡🟠🔴</span>
                  <span className="legend-text">Quality: High → Medium → Low → Poor</span>
                </div>
              </div>
            </div>
            
            {sheetPreview.totalRows > sheetPreview.data.length && (
              <div className="preview-info">
                Showing first {sheetPreview.data.length} of {sheetPreview.totalRows} rows
              </div>
            )}
          </div>
        </>
      )}

      {/* Smart Matching Examples */}
      <div className="smart-matching-info">
        <h6>🧠 Smart Matching Examples</h6>
        <div className="matching-examples">
          <div className="example-item">
            <span className="example-excel">"Hole Size (in)"</span>
            <span className="example-arrow">→</span>
            <span className="example-target">"Bit Size"</span>
            <span className="example-note">Units removed, normalized</span>
          </div>
          <div className="example-item">
            <span className="example-excel">"Outside Diameter"</span>
            <span className="example-arrow">→</span>
            <span className="example-target">"OD"</span>
            <span className="example-note">Abbreviation match</span>
          </div>
          <div className="example-item">
            <span className="example-excel">"Serial Number"</span>
            <span className="example-arrow">→</span>
            <span className="example-target">"Serial No"</span>
            <span className="example-note">Synonym match</span>
          </div>
          <div className="example-item">
            <span className="example-excel">"Top Depth"</span>
            <span className="example-arrow">→</span>
            <span className="example-target">"Depth In"</span>
            <span className="example-note">Context match</span>
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={() => setStep('sheets')}>
          ← Change Sheet
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleExtractData}
          disabled={isProcessing}
        >
          {isProcessing ? 'Extracting...' : 'Extract Data'}
        </button>
      </div>
    </div>
  );

  // Results Step Component
  const ResultsStep = () => {
    if (!extractionResults) return null;

    const { results, summary } = extractionResults;
    const mappings = results.mappings || {};
    const metadata = results.metadata || {};

    return (
      <div className="excel-step results-step">
        <div className="step-header">
          <h3>📊 Extraction Results</h3>
          <p>Review extracted data and apply to your form</p>
        </div>

        {/* Manual Mapping Section - grouped by Parent and Child fields */}
        <div className="manual-mapping-section">
          {parentLabels.length > 0 && (
            <>
              <h5>✏️ Parent Fields</h5>
              <table className="manual-mapping-table">
                <thead>
                  <tr><th>Label</th><th>Value</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {parentLabels.map(label => {
                    const currentVal = manualMappings[label] ?? (mappings[label] || '');
                    return (
                      <tr key={label} className="manual-mapping-row">
                        <td className="manual-mapping-label">{label}</td>
                        <td className="manual-mapping-value">{currentVal || <em>empty</em>}</td>
                        <td className="manual-mapping-action">
                          <button
                            className="btn btn-sm btn-outline-primary map-btn"
                            onClick={() => { setMappingMode(label); setStep('extract'); }}
                          >Map</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
          {childLabels.length > 0 && (
            <>
              <h5>✏️ Child Fields</h5>
              <table className="manual-mapping-table">
                <thead>
                  <tr><th>Label</th><th>Value</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {childLabels.map(label => {
                    const currentVal = manualMappings[label] ?? (mappings[label] || '');
                    return (
                      <tr key={label} className="manual-mapping-row">
                        <td className="manual-mapping-label">{label}</td>
                        <td className="manual-mapping-value">{currentVal || <em>empty</em>}</td>
                        <td className="manual-mapping-action">
                          <button
                            className="btn btn-sm btn-outline-primary map-btn"
                            onClick={() => { setMappingMode(label); setStep('extract'); }}
                          >Map</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
        {/* Mapping Templates Management */}
        <div className="template-management">
          <h5>📂 Mapping Templates</h5>
          <div className="templates-list">
            {templates.map(t => (
              <div key={t.name} className="template-item">
                <span className="template-name">{t.name}</span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => applyTemplate(t.mappings)}
                >Apply</button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteTemplate(t.name)}
                >Delete</button>
              </div>
            ))}
            {templates.length === 0 && <p>No templates saved.</p>}
          </div>
          <div className="save-template">
            <input
              type="text"
              placeholder="Template name"
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
            />
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSaveTemplate}
            >Save Template</button>
          </div>
        </div>
        <div className="results-summary">
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{summary.foundLabels}</span>
              <span className="stat-label">Labels Found</span>
            </div>
            <div className="stat">
              <span className="stat-value">{summary.totalLabels}</span>
              <span className="stat-label">Total Searched</span>
            </div>
            <div className="stat">
              <span className="stat-value">{summary.successRate}</span>
              <span className="stat-label">Success Rate</span>
            </div>
          </div>
        </div>

        <div className="extracted-data">
          <h5>📋 Backend Extraction Results</h5>
          {Object.keys(mappings).length > 0 ? (
            <div className="mappings-list">
              {Object.entries(mappings).map(([label, value]) => (
                <div key={label} className="mapping-item">
                  <div className="mapping-label">{label}</div>
                  <div className="mapping-arrow">→</div>
                  <div className="mapping-value">
                    {value || <em>empty</em>}
                  </div>
                  <div className="mapping-meta">
                    <span className="extraction-method">
                      {metadata.extractionMethod?.[label] || 'unknown'}
                    </span>
                    <span className="confidence">
                      {Math.round((metadata.confidence?.[label] || 0) * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-mappings">
              <span className="no-data-icon">📭</span>
              <p>No data mappings found by backend</p>
            </div>
          )}
        </div>

        {metadata.suggestions && metadata.suggestions.length > 0 && (
          <div className="suggestions">
            <h5>💡 Suggestions</h5>
            <ul>
              {metadata.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="step-actions">
          <button className="btn btn-secondary" onClick={() => setStep('extract')}>
            ← Reconfigure
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleApplyResults}
            disabled={(!livePreview?.mappings?.length) && (Object.keys(mappings).length === 0)}
          >
            {livePreview?.mappings?.length > 0 
              ? `Apply Frontend Detection (${livePreview.mappings.length} mappings)` 
              : `Apply Backend Results (${Object.keys(mappings).length} mappings)`
            }
          </button>
        </div>
      </div>
    );
  };

  // Expandable panel state
  const [isExpanded, setIsExpanded] = useState(true);

  // Expand/collapse handler
  const togglePanel = () => setIsExpanded((prev) => !prev);

  // Main render
  return (
    <div className="excel-mapping-pro-panel">
      <div className="excel-mapping-header">
        <div className="header-left">
          <span className="excel-mapping-title">{title || 'Excel Data Extraction'}</span>
          <span className="excel-mapping-step">{step === 'sheets' ? 'Select Sheet' : step === 'scan-results' ? 'Scan Results' : step === 'mapping' ? 'Extract Options' : step === 'manual' || step === 'extract' ? 'Results' : ''}</span>
        </div>
        <button className="excel-mapping-close" onClick={handleCancel} title="Close">×</button>
      </div>
      <div className="excel-mapping-content">
        {/* Step rendering logic with new layout */}
        {step === 'upload' && <UploadStep />}
        {step === 'sheets' && (
          <div className="excel-mapping-sheet-list">
            <SheetSelectionStep />
          </div>
        )}
        {step === 'scan-results' && (
          <div className="excel-mapping-fullwidth">
            <ScanResultsStep />
          </div>
        )}
        {step === 'mapping' && <FieldMappingStep />}
        {(step === 'manual' || step === 'extract') && <ResultsStep />}
      </div>
      {/* Sticky footer for actions (if needed) */}
      {/* Place action buttons here if not already in step components */}
    </div>
  );
};

export default DataMappingExcel;